import { useState, useCallback, useEffect } from 'react';
import {
  Button,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Chip,
  Tooltip,
  Spinner,
} from '@nextui-org/react';
import { toast } from 'react-toastify';
import { Icon } from '@iconify/react';
import useUserStore from '../../../../common/src/stores/useUserStore';
import {
  getExporterMappingProfiles,
  ColumnMappingProfile,
  deleteMappingProfile,
  updateMappingProfile,
} from 'common/src/api/column-mappings';
import { read, utils, WorkSheet } from 'xlsx';
import ColumnMappingModal from '../shipments/column-mapping-modal';
import { FieldMapping, MappingSuggestion } from '../../utils/column-matcher';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import { EmptyState, LoadingState } from '../ui';

// Helper functions from excel-upload-modal
const extractHeadersFromRow = (
  worksheet: WorkSheet,
  headerRow: number
): string[] => {
  const range = utils.decode_range(worksheet['!ref'] || 'A1');
  const headers: string[] = [];

  const rowIndex = headerRow - 1;

  for (let col = range.s.c; col <= range.e.c; col++) {
    const cellAddress = utils.encode_cell({ r: rowIndex, c: col });
    const cell = worksheet[cellAddress];

    if (cell && cell.v) {
      headers.push(String(cell.v).trim());
    } else {
      break;
    }
  }

  return headers.filter((h) => h.length > 0);
};

const remapDataWithHeaders = (
  data: Record<string, unknown>[],
  actualHeaders: string[]
): Record<string, unknown>[] => {
  return data.map((row) => {
    const remappedRow: Record<string, unknown> = {};
    const oldKeys = Object.keys(row);

    actualHeaders.forEach((header, index) => {
      if (index < oldKeys.length) {
        remappedRow[header] = row[oldKeys[index]];
      }
    });

    return remappedRow;
  });
};

export const PlanillaManagementSection = () => {
  const { t } = useTranslation('settings');
  const { userProfile } = useUserStore();
  const [profiles, setProfiles] = useState<ColumnMappingProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedProfile, setSelectedProfile] =
    useState<ColumnMappingProfile | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Excel file state
  const [exporterColumns, setExporterColumns] = useState<string[]>([]);
  const [sampleData, setSampleData] = useState<Record<string, unknown>[]>([]);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [mappingSuggestions, setMappingSuggestions] = useState<
    MappingSuggestion[]
  >([]);

  // Load profiles
  const loadProfiles = useCallback(async () => {
    if (!userProfile?.exporter_id) return;

    setIsLoading(true);
    try {
      const data = await getExporterMappingProfiles(userProfile.exporter_id);
      setProfiles(data);
    } catch (error) {
      console.error('Error loading profiles:', error);
      toast.error(t('Error al cargar las planillas'));
    } finally {
      setIsLoading(false);
    }
  }, [userProfile?.exporter_id]);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  // Handle file upload
  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file || !selectedProfile) return;

    setIsProcessing(true);

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = read(data, { type: 'array' });

          // Use the profile's sheet and header row settings
          const worksheet = workbook.Sheets[selectedProfile.sheet_name];

          if (!worksheet) {
            throw new Error(
              t('La hoja no existe en el archivo', { sheet: selectedProfile.sheet_name })
            );
          }

          // Extract headers
          const columns = extractHeadersFromRow(
            worksheet,
            selectedProfile.header_row
          );

          // Get sample data
          const jsonData = utils.sheet_to_json(worksheet, {
            raw: true,
            header: selectedProfile.header_row - 1,
            defval: '',
          }) as Record<string, unknown>[];

          const dataRows = jsonData.slice(selectedProfile.header_row);
          const remappedData = remapDataWithHeaders(dataRows, columns);

          setExporterColumns(columns);
          setSampleData(remappedData.slice(0, 3));

          // Convert current mapping_config to suggestions format for editing
          // mapping_config is { systemField: exporterColumn }
          const currentMappings: MappingSuggestion[] = Object.entries(
            selectedProfile.mapping_config
          ).map(([systemField, exporterColumn]) => ({
            exporterColumn: exporterColumn as string,
            suggestedField: systemField,
            confidence: 'high' as const,
            alternatives: [],
          }));

          setMappingSuggestions(currentMappings);

          setIsProcessing(false);
          setShowEditModal(false);
          setShowMappingModal(true);
        } catch (error) {
          setIsProcessing(false);
          const errorMessage =
            error instanceof Error
              ? error.message
              : t('Error al procesar el archivo');
          toast.error(errorMessage);
        }
      };
      reader.readAsArrayBuffer(file);
    } catch (error) {
      setIsProcessing(false);
      toast.error(t('Error al leer el archivo'));
    }
  };

  // Handle edit button click
  const handleEditClick = (profile: ColumnMappingProfile) => {
    setSelectedProfile(profile);
    setShowEditModal(true);
  };

  // Handle delete button click
  const handleDeleteClick = (profile: ColumnMappingProfile) => {
    setSelectedProfile(profile);
    setShowDeleteConfirm(true);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (!selectedProfile) return;

    try {
      await deleteMappingProfile(selectedProfile.id);
      toast.success(t('Planilla eliminada correctamente'));
      loadProfiles();
      setShowDeleteConfirm(false);
      setSelectedProfile(null);
    } catch (error) {
      console.error('Error deleting profile:', error);
      toast.error(t('Error al eliminar la planilla'));
    }
  };

  // Handle mapping confirmation (update)
  const handleMappingConfirm = async (
    mapping: FieldMapping,
    profileName: string | null,
    packingName: string | null,
    dateFormats: Record<string, string>,
    isMultiPacking: boolean
  ) => {
    if (!selectedProfile || !profileName || (!packingName && !isMultiPacking)) {
      toast.error(t('Faltan datos requeridos'));
      return;
    }

    const transforms = { dateFormats, isMultiPacking };

    try {
      await updateMappingProfile(
        selectedProfile.id,
        profileName,
        selectedProfile.sheet_name,
        selectedProfile.header_row,
        mapping,
        packingName || undefined,
        transforms
      );
      toast.success(t('Planilla actualizada correctamente'));
      loadProfiles();
      setShowMappingModal(false);
      setSelectedProfile(null);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(t('Error al actualizar la planilla'));
    }
  };

  return (
    <div className='w-full'>
      <div className='mb-4'>
        <h3 className='text-lg font-semibold'>{t('Gestión de Planillas')}</h3>
        <p className='text-sm text-default-500'>
          {t('Edita o elimina las configuraciones de planillas guardadas')}
        </p>
      </div>

      {isLoading ? (
        <LoadingState />
      ) : profiles.length === 0 ? (
        <EmptyState title={t('No hay planillas guardadas')} />
      ) : (
        <Table aria-label={t('Tabla de planillas')}>
          <TableHeader>
            <TableColumn>{t('Nombre')}</TableColumn>
            <TableColumn>{t('Planta')}</TableColumn>
            <TableColumn>{t('Hoja')}</TableColumn>
            <TableColumn>{t('Fila Encabezados')}</TableColumn>
            <TableColumn>{t('Fecha Creación')}</TableColumn>
            <TableColumn>{t('Acciones')}</TableColumn>
          </TableHeader>
          <TableBody>
            {profiles.map((profile) => (
              <TableRow key={profile.id}>
                <TableCell>
                  <span className='font-medium'>{profile.profile_name}</span>
                </TableCell>
                <TableCell>
                  {(profile.transforms as any)?.isMultiPacking ? (
                    <Chip size='sm' variant='flat' color='secondary'>
                      {t('Múltiples Plantas')}
                    </Chip>
                  ) : (
                    <Chip size='sm' variant='flat'>
                      {profile.packing_name}
                    </Chip>
                  )}
                </TableCell>
                <TableCell>{profile.sheet_name}</TableCell>
                <TableCell>{profile.header_row}</TableCell>
                <TableCell>
                  {new Date(profile.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <div className='flex gap-2'>
                    <Tooltip content={t('Editar')}>
                      <Button
                        isIconOnly
                        size='sm'
                        variant='light'
                        color='primary'
                        onPress={() => handleEditClick(profile)}
                      >
                        <Icon icon='tabler:edit' />
                      </Button>
                    </Tooltip>
                    <Tooltip content={t('Eliminar')}>
                      <Button
                        isIconOnly
                        size='sm'
                        variant='light'
                        color='danger'
                        onPress={() => handleDeleteClick(profile)}
                      >
                        <Icon icon='tabler:trash' />
                      </Button>
                    </Tooltip>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Edit Modal - File Upload */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedProfile(null);
        }}
        size='lg'
      >
        <ModalContent>
          <ModalHeader>
            {t('Editar Planilla')}: {selectedProfile?.profile_name}
          </ModalHeader>
          <ModalBody>
            <div className='flex flex-col gap-4'>
              <div className='p-4 bg-blue-50 dark:bg-blue-950 rounded-lg'>
                <p className='text-sm text-default-600 mb-2'>
                  <strong>{t('Configuración actual')}:</strong>
                </p>
                <ul className='text-sm text-default-500 space-y-1'>
                  <li>• {t('Hoja')}: {selectedProfile?.sheet_name}</li>
                  <li>• {t('Fila de encabezados')}: {selectedProfile?.header_row}</li>
                  <li>• {t('Planta')}: {(selectedProfile?.transforms as any)?.isMultiPacking ? t('Múltiples Plantas') : selectedProfile?.packing_name}</li>
                </ul>
              </div>

              <div className='p-4 bg-default-100 rounded-lg max-h-64 overflow-y-auto'>
                <p className='text-sm text-default-600 mb-2'>
                  <strong>{t('Mapeo de columnas actual')}:</strong>
                </p>
                <div className='text-xs text-default-500 space-y-1'>
                  {selectedProfile?.mapping_config &&
                    Object.keys(selectedProfile.mapping_config).length > 0 ? (
                    Object.entries(selectedProfile.mapping_config).map(
                      ([key, value]) => (
                        <div
                          key={key}
                          className='flex justify-between py-1 border-b border-divider'
                        >
                          <span className='font-medium'>{key}:</span>
                          <span className='text-default-400'>
                            {(value as string) || t('(sin mapear)')}
                          </span>
                        </div>
                      )
                    )
                  ) : (
                    <p className='text-default-400'>{t('No hay mapeos configurados')}</p>
                  )}
                </div>
              </div>

              <p className='text-sm text-default-500'>
                {t('Sube un archivo Excel de ejemplo para editar el mapeo de columnas')}
              </p>

              <input
                type='file'
                accept='.xlsx,.xls'
                onChange={handleFileSelect}
                className='block w-full text-sm text-default-400
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-lg file:border-0
                  file:text-sm file:font-semibold
                  file:bg-primary file:text-white
                  hover:file:bg-primary-600
                  cursor-pointer'
              />

              {isProcessing && (
                <div className='flex items-center gap-3 p-4 bg-indigo-50 rounded-lg'>
                  <Spinner size='md' />
                  <span className='text-sm text-indigo-900'>
                    {t('Procesando archivo...')}
                  </span>
                </div>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              variant='light'
              onPress={() => {
                setShowEditModal(false);
                setSelectedProfile(null);
              }}
            >
              {t('Cancelar')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setSelectedProfile(null);
        }}
        size='sm'
      >
        <ModalContent>
          <ModalHeader>{t('Confirmar Eliminación')}</ModalHeader>
          <ModalBody>
            <p>
              {t('¿Estás seguro de que deseas eliminar la planilla?', { name: selectedProfile?.profile_name })}
            </p>
            <p className='text-sm text-default-500 mt-2'>
              {t('Esta acción no se puede deshacer.')}
            </p>
          </ModalBody>
          <ModalFooter>
            <Button
              variant='light'
              onPress={() => {
                setShowDeleteConfirm(false);
                setSelectedProfile(null);
              }}
            >
              {t('Cancelar')}
            </Button>
            <Button color='danger' onPress={handleConfirmDelete}>
              {t('Eliminar')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Column Mapping Modal */}
      {showMappingModal && selectedProfile && (
        <ColumnMappingModal
          isOpen={showMappingModal}
          onClose={() => {
            setShowMappingModal(false);
            setSelectedProfile(null);
          }}
          exporterColumns={exporterColumns}
          sampleData={sampleData}
          initialSuggestions={mappingSuggestions}
          onConfirm={handleMappingConfirm}
          profileName={selectedProfile.profile_name}
          packingName={selectedProfile.packing_name || ''}
          availableSheets={[selectedProfile.sheet_name]}
          selectedSheet={selectedProfile.sheet_name}
          headerRow={selectedProfile.header_row}
          showSheetSelector={false}
          initialDateFormats={(selectedProfile.transforms as any)?.dateFormats}
        />
      )}
    </div>
  );
};
